import type { ColumnDef } from "@tanstack/react-table";
import type { TrackRow } from "#shared/schemas/trackQuery.ts";
import { StatusChip } from "@web/components/StatusChip.tsx";
import { ArtistNamesCell } from "@web/features/grid/ArtistNamesCell.tsx";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import { TitlePlayCell } from "@web/features/player/TitlePlayCell.tsx";
import { formatBpm, formatDate, formatDuration } from "@web/lib/format.ts";
import { cn } from "@web/lib/utils.ts";

export type GridColumnMeta = {
  align?: "left" | "right";
  editable?: "status";
  /** Grow to fill leftover horizontal space. Title/Artist/Album. */
  flex?: number;
};

const meta = (m: GridColumnMeta): GridColumnMeta => m;

function AlbumNameCell({ album }: { album: TrackRow["album"] }) {
  const openDetail = useOpenDetail();
  if (!album?.id) {
    return <span className="truncate text-muted">—</span>;
  }
  return (
    <button
      type="button"
      className="max-w-full truncate text-left text-muted hover:text-text hover:underline"
      title={album.name ?? undefined}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        openDetail({ type: "album", id: album.id, label: album.name ?? "Album" });
      }}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {album.name ?? "—"}
    </button>
  );
}

/**
 * The library grid shows song facts — not every custom field. Custom fields live
 * in the detail sheet (phase 11) and the bulk action bar; dumping them into the
 * table made seed leftovers like Script / Priority look like product columns.
 */
export function buildTrackColumns(options?: {
  /** All-tracks view: mark titles that are not in Liked Songs. */
  markNotLiked?: boolean;
}): ColumnDef<TrackRow, unknown>[] {
  const markNotLiked = options?.markNotLiked === true;
  return [
    {
      id: "track.name",
      accessorFn: (row) => row.name,
      header: "Title",
      size: 320,
      minSize: 160,
      cell: ({ row }) => {
        const track = row.original;
        return (
          <TitlePlayCell
            trackId={track.id}
            name={track.name}
            coverUrl={track.cover_url ?? track.album?.cover_url ?? null}
            notLiked={markNotLiked ? !track.liked : undefined}
          />
        );
      },
      meta: meta({ flex: 3 }),
    },
    {
      id: "artist.name",
      accessorFn: (row) =>
        row.artists
          .map((a) => a.name)
          .filter(Boolean)
          .join(", "),
      header: "Artist",
      size: 220,
      minSize: 120,
      cell: ({ row }) => <ArtistNamesCell artists={row.original.artists} />,
      meta: meta({ flex: 2 }),
    },
    {
      id: "album.name",
      accessorFn: (row) => row.album?.name ?? "",
      header: "Album",
      size: 220,
      minSize: 120,
      cell: ({ row }) => <AlbumNameCell album={row.original.album} />,
      meta: meta({ flex: 2 }),
    },
    {
      id: "track.duration_ms",
      accessorFn: (row) => row.duration_ms,
      header: "Length",
      size: 72,
      minSize: 56,
      enableResizing: false,
      cell: ({ row }) => formatDuration(row.original.duration_ms),
      meta: meta({ align: "right" }),
    },
    {
      id: "album.release_year",
      accessorFn: (row) => {
        const d = row.album?.release_date;
        if (!d) return null;
        const y = Number(d.slice(0, 4));
        return Number.isFinite(y) ? y : null;
      },
      header: "Year",
      size: 72,
      minSize: 56,
      enableResizing: false,
      cell: ({ getValue }) => {
        const v = getValue();
        return typeof v === "number" ? v : "—";
      },
      meta: meta({ align: "right" }),
    },
    {
      id: "track.added_at",
      accessorFn: (row) => row.added_at,
      header: "Date added",
      size: 110,
      minSize: 90,
      enableResizing: false,
      cell: ({ row }) => (
        <span className="text-muted">{formatDate(row.original.added_at)}</span>
      ),
      meta: meta({}),
    },
    {
      id: "status.status",
      accessorFn: (row) => row.status?.status ?? "none",
      header: "Status",
      size: 150,
      minSize: 120,
      cell: ({ row }) => <StatusChip status={row.original.status?.status} />,
      meta: meta({ editable: "status" }),
    },
    {
      id: "audio.tempo",
      accessorFn: (row) => row.bpm,
      header: "BPM",
      size: 72,
      minSize: 60,
      enableResizing: false,
      cell: ({ row }) => {
        const manual = row.original.bpm_manual;
        return (
          <span
            title={manual ? "Manual BPM" : undefined}
            className={cn("tabular-nums", manual ? "text-status-working" : undefined)}
          >
            {formatBpm(row.original.bpm)}
          </span>
        );
      },
      meta: meta({ align: "right" }),
    },
  ];
}
